import { ListIcon, XIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { copy } from '@/content/copy';
import { CONTACT_URL, DOCS_URL, isExternalUrl } from '@/lib/env';
import { ScrollTrigger, useGSAP } from '@/lib/gsap';
import { useMediaQuery } from '@/scroll/use-media-query';
import { useScroll, useSectionLink, type SectionLinkProps } from '@/scroll/use-scroll';

/** Scroll distance in px after which the bar gains its blurred tint (`.nav-bar[data-scrolled]`). */
const TINT_AFTER = 32;

/** Tailwind's `md`: from here the section links sit inline and the menu does not exist. */
const INLINE_LINKS = '(min-width: 768px)';

const MENU_ID = 'nav-menu';

const shellClass = 'mx-auto max-w-[1180px] px-[clamp(1.4rem,4vw,3.2rem)]';

const menuLinkClass =
  'hud block border-b border-border/60 py-4 text-[0.8rem] text-foreground/80 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none';

/**
 * Fixed bar: the logo, the section links (inline from `md`, in a drop-down menu below it),
 * Docs and the contact CTA. The bar tints once the page scrolls and while the menu is open.
 */
export function Nav() {
  const ref = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { scrollToTop, lockScroll } = useScroll();
  const sectionLink = useSectionLink();
  const inlineLinks = useMediaQuery(INLINE_LINKS);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      // Enter/leave-back rather than onToggle: a trigger is inactive at progress 1, and on a
      // short page the bottom is exactly `max`, which would drop the tint at the footer.
      ScrollTrigger.create({
        start: TINT_AFTER,
        end: 'max',
        onEnter: () => el.setAttribute('data-scrolled', ''),
        onLeaveBack: () => el.removeAttribute('data-scrolled'),
      });
    },
    { scope: ref },
  );

  // The lock travels with the state, synchronously: a menu link unlocks before it scrolls.
  const setMenu = useCallback(
    (next: boolean) => {
      setOpen(next);
      lockScroll(next);
    },
    [lockScroll],
  );

  // The menu only exists below `md`; a viewport growing past it would leave the page locked.
  useEffect(() => {
    if (inlineLinks) setMenu(false);
  }, [inlineLinks, setMenu]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenu(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setMenu]);

  const onBrandClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setMenu(false);
    scrollToTop();
  };

  /** A section link inside the menu: closes it first, since Lenis ignores scrolls while locked. */
  const menuLink = (id: string): SectionLinkProps => {
    const link = sectionLink(id);
    return {
      ...link,
      onClick: (event) => {
        setMenu(false);
        link.onClick(event);
      },
    };
  };

  // An in-page destination (the default #contact) scrolls with Lenis like the other links;
  // any other leaves the page (or opens a tab), so the menu copy closes on the way out.
  const contactLink = CONTACT_URL.startsWith('#')
    ? sectionLink(CONTACT_URL.slice(1))
    : { href: CONTACT_URL, target: isExternalUrl(CONTACT_URL) ? '_blank' : undefined };
  const menuContactLink = CONTACT_URL.startsWith('#')
    ? menuLink(CONTACT_URL.slice(1))
    : { ...contactLink, onClick: () => setMenu(false) };

  return (
    <>
      <header
        ref={ref}
        data-open={open ? '' : undefined}
        className="nav-bar fixed inset-x-0 top-0 z-50"
      >
        <div className={`${shellClass} flex items-center justify-between gap-6 py-5`}>
          <a
            href="#top"
            onClick={onBrandClick}
            aria-label={`${copy.brand.name}, ${copy.a11y.backToTop}`}
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
          >
            <Logo size="nav" />
          </a>

          <div className="flex items-center gap-[clamp(1.1rem,3vw,2.4rem)]">
            <nav
              aria-label={copy.a11y.primaryNav}
              className="hidden items-center gap-[clamp(1.1rem,3vw,2.4rem)] md:flex"
            >
              {copy.nav.links.map((link) => (
                <a
                  key={link.id}
                  className="nav-link hud text-foreground/75 hover:text-foreground"
                  {...sectionLink(link.id)}
                >
                  {link.label}
                </a>
              ))}
              <a href={DOCS_URL} className="nav-link hud text-foreground/75 hover:text-foreground">
                {copy.nav.docs}
              </a>
            </nav>
            {/* On a phone the CTA moves into the menu: the bar has no room beside the logo. */}
            <Button {...contactLink} size="sm" className="hidden sm:inline-flex">
              {copy.nav.cta}
            </Button>
            <button
              ref={toggleRef}
              type="button"
              aria-expanded={open}
              aria-controls={MENU_ID}
              aria-label={open ? copy.a11y.menuClose : copy.a11y.menuOpen}
              onClick={() => setMenu(!open)}
              className="-mr-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background md:hidden"
            >
              {open ? (
                <XIcon aria-hidden weight="bold" className="h-5 w-5" />
              ) : (
                <ListIcon aria-hidden weight="bold" className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {open ? (
          <nav
            id={MENU_ID}
            aria-label={copy.a11y.primaryNav}
            className="border-t border-border/70 motion-safe:animate-panel-enter md:hidden"
          >
            <div className={`${shellClass} pb-6 pt-1`}>
              <ul>
                {copy.nav.links.map((link) => (
                  <li key={link.id}>
                    <a className={menuLinkClass} {...menuLink(link.id)}>
                      {link.label}
                    </a>
                  </li>
                ))}
                <li>
                  <a href={DOCS_URL} className={menuLinkClass}>
                    {copy.nav.docs}
                  </a>
                </li>
              </ul>
              <Button {...menuContactLink} className="mt-6 w-full sm:hidden">
                {copy.nav.cta}
              </Button>
            </div>
          </nav>
        ) : null}
      </header>

      {/* Outside the header: its backdrop blur would contain a fixed child to the bar's box. */}
      {open ? (
        <div
          aria-hidden
          onClick={() => setMenu(false)}
          className="fixed inset-0 z-40 bg-background/60 motion-safe:animate-fade-in md:hidden"
        />
      ) : null}
    </>
  );
}
